import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { useState, useEffect, useCallback, forwardRef } from 'react'
import { Box } from 'theme-ui'
import { DayPickerSingleDateController, isSameDay } from 'react-dates'
import moment from 'moment'
import { CalendarNavChevron } from 'components/Icon'
import { noop } from 'utils'

function NavNext() {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: 24,
        height: 24,
        backgroundColor: 'primary',
        borderRadius: '50%',
        right: 32,
        top: 24,
      }}>
      <CalendarNavChevron />
    </Box>
  )
}

function NavPrev() {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: 24,
        height: 24,
        backgroundColor: 'primary',
        borderRadius: '50%',
        right: 64,
        top: 24,
        transform: 'rotate(-180deg)',
      }}>
      <CalendarNavChevron />
    </Box>
  )
}

export type CalendarProps = {
  initialDate?: string
  minimumDate?: string
  maximumDate?: string
  onDateChange?: (date: moment.Moment) => void
}

export const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  ({ initialDate, onDateChange = noop, minimumDate, maximumDate }, ref) => {
    const [date, setDate] = useState<moment.Moment | null>(
      initialDate ? moment(initialDate) : null
    )
    const [focused, setFocused] = useState<boolean>(true)

    useEffect(() => {
      setDate(initialDate ? moment(initialDate) : null)
    }, [initialDate])

    //This gets called for every date +/-1 month
    //for some reason, this sometimes gets called 3 times per date...
    const handleOutsideRange = useCallback(
      (day: moment.Moment) => {
        day = moment(day.format('YYYY-MM-DD')) //aligning the formats of the three dates for comparison
        const minDate = minimumDate ? moment(minimumDate) : null
        const maxDate = maximumDate ? moment(maximumDate) : null
        if (minimumDate && day.isBefore(minDate)) {
          return true
        } else if (maximumDate && day.isAfter(maxDate)) {
          return true
        } else {
          return false
        }
      },
      [minimumDate, maximumDate]
    )

    function handleDateChange(value: moment.Moment | null) {
      if (value) {
        setDate(value)
        onDateChange(value)
      }
    }

    function handleOnFocusChange() {
      // Force the focused states to always be truthy
      // so that date is always selectable
      setFocused(true)
    }
    const initialVisibleMonth = () => date ?? moment() //use the selected date, or the current day to select the init visible month

    return (
      <Box
        ref={ref}
        sx={{
          bg: 'white',
          borderRadius: '3px',
          boxShadow: '0 2px 6px rgb(0 0 0 / 5%), 0 0 0 1px rgb(0 0 0 / 7%)',
        }}>
        <DayPickerSingleDateController
          date={date}
          focused={focused}
          initialVisibleMonth={initialVisibleMonth}
          isDayHighlighted={(d) => (date ? isSameDay(d, date) : false)}
          enableOutsideDays={false}
          isOutsideRange={handleOutsideRange}
          daySize={44}
          navNext={<NavNext />}
          navPrev={<NavPrev />}
          onDateChange={handleDateChange}
          onFocusChange={handleOnFocusChange}
          hideKeyboardShortcutsPanel
        />
      </Box>
    )
  }
)
